import React from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import '../styles/MarketCards.css';

/**
 * MarketCard - Reusable card component with expand-in-place behavior
 *
 * @param {string} title - Card title
 * @param {React.ReactNode} icon - Icon component to display
 * @param {React.ReactNode} summary - Content shown when collapsed
 * @param {React.ReactNode} children - Content shown when expanded
 * @param {boolean} isExpanded - Whether card is currently expanded
 * @param {function} onToggle - Callback when card is clicked
 * @param {string} className - Additional CSS classes
 */
const MarketCard = ({
  title,
  icon,
  summary,
  children,
  isExpanded,
  onToggle,
  className = '',
  isLoading = false,
  error = null
}) => {
  return (
    <div
      className={`market-card ${isExpanded ? 'market-card--expanded' : ''} ${className}`}
    >
      {/* Card Header - Always visible, clickable */}
      <div
        className="market-card__header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="market-card__title-row">
          {icon && <span className="market-card__icon">{icon}</span>}
          <h3 className="market-card__title">{title}</h3>
          <span className="market-card__chevron">
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </div>

        {/* Summary - Only shown when collapsed */}
        {!isExpanded && (
          <div className="market-card__summary">
            {isLoading ? (
              <div className="market-card__loading">Loading...</div>
            ) : error ? (
              <div className="market-card__error">Error loading data</div>
            ) : (
              summary
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="market-card__content">
          {isLoading ? (
            <div className="market-card__loading">Loading...</div>
          ) : error ? (
            <div className="market-card__error">
              Error: {error.message || 'Failed to load data'}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

export default MarketCard;
